import type { FC, FormEvent } from 'react';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { groupsApi } from 'src/shared/api/groupsApi';
import { setError, setSuccess } from 'src/shared/lib/utils';
import type { UserRole } from 'src/shared/types/groups';
import { Group } from 'src/shared/types/groups';
import { Button, Input, Modal } from 'src/shared/ui';

import { MembersTab } from './components/MembersTab';
import { ProjectsTab } from './components/ProjectsTab';
import {
  CreateGroupErrors,
  CreateGroupValues,
  hasValidationErrors,
  RegisterUserErrors,
  RegisterUserValues,
  validateCreateGroupValues,
  validateRegisterUserValues,
} from './lib/validation';

type GroupsTabKey = 'members' | 'projects';

const initialCreateGroupValues: CreateGroupValues = {
  name: '',
  description: '',
};

const initialRegisterUserValues: RegisterUserValues = {
  email: '',
  full_name: '',
  user_name: '',
  role: '',
};

const roleOptions: Array<{ label: string; value: UserRole }> = [
  { label: 'Дизайнер форм', value: 'form_designer' },
  { label: 'Пользователь отчетов', value: 'report_viewer' },
  { label: 'Оператор', value: 'data_operator' },
  { label: 'Админ', value: 'admin' },
];

const nativeFieldClassName =
  'font-gothampro-400 h-11 w-full rounded border border-violet-500 bg-white px-4 text-black ' +
  'transition-all focus:border-blue-100 focus:outline-none';

const textareaFieldClassName =
  'font-gothampro-400 min-h-28 w-full rounded border border-violet-500 bg-white px-4 py-3 ' +
  'text-black transition-all focus:border-blue-100 focus:outline-none';

const labelClassName = 'font-gothampro-400 mb-1 ml-1 text-xs leading-3 text-grey-300';
const errorClassName = 'font-gothampro-400 mt-1 text-extra-xs text-pink-100';

export const GroupsManagement: FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [activeTab, setActiveTab] = useState<GroupsTabKey>('members');
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [isRegisterUserModalOpen, setIsRegisterUserModalOpen] = useState(false);
  const [createGroupValues, setCreateGroupValues] =
    useState<CreateGroupValues>(initialCreateGroupValues);
  const [createGroupErrors, setCreateGroupErrors] = useState<CreateGroupErrors>({});
  const [registerUserValues, setRegisterUserValues] =
    useState<RegisterUserValues>(initialRegisterUserValues);
  const [registerUserErrors, setRegisterUserErrors] = useState<RegisterUserErrors>({});

  const fetchGroups = async () => {
    try {
      const { data } = await groupsApi.getGroups();
      setGroups(data);

      if (selectedGroup) {
        const updatedGroup = data.find(group => group.id === selectedGroup.id);
        setSelectedGroup(updatedGroup || null);
      }
    } catch (error) {
      setError('Ошибка при загрузке групп');
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const closeCreateGroupModal = () => {
    setIsCreateGroupModalOpen(false);
    setCreateGroupValues(initialCreateGroupValues);
    setCreateGroupErrors({});
  };

  const closeRegisterUserModal = () => {
    setIsRegisterUserModalOpen(false);
    setRegisterUserValues(initialRegisterUserValues);
    setRegisterUserErrors({});
  };

  const handleCreateGroupInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;

    setCreateGroupValues(prev => ({
      ...prev,
      [name]: value,
    }));

    setCreateGroupErrors(prev => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const handleRegisterUserChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;

    setRegisterUserValues(prev => ({
      ...prev,
      [name]: value,
    }));

    setRegisterUserErrors(prev => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const handleCreateGroup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors = validateCreateGroupValues(createGroupValues);
    setCreateGroupErrors(errors);

    if (hasValidationErrors(errors)) {
      return;
    }

    try {
      await groupsApi.createGroup({
        group_name: createGroupValues.name.trim(),
        group_description: createGroupValues.description.trim(),
      });

      setSuccess('Группа создана успешно');
      await fetchGroups();
      closeCreateGroupModal();
    } catch (error) {
      setError('Ошибка при создании группы');
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    try {
      await groupsApi.deleteGroup(groupId);
      setSuccess('Группа удалена успешно');
      await fetchGroups();
    } catch (error) {
      setError('Ошибка при удалении группы');
    }
  };

  const handleRegisterUser = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors = validateRegisterUserValues(registerUserValues);
    setRegisterUserErrors(errors);

    if (hasValidationErrors(errors)) {
      return;
    }

    try {
      await groupsApi.registerUser({
        email: registerUserValues.email.trim(),
        full_name: registerUserValues.full_name.trim(),
        user_name: registerUserValues.user_name.trim(),
        role: registerUserValues.role as UserRole,
      });

      setSuccess('Пользователь успешно зарегистрирован. Пароль отправлен на указанный email');
      closeRegisterUserModal();
      await fetchGroups();
    } catch (error) {
      setError('Ошибка при регистрации пользователя');
    }
  };

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex items-center justify-between p-6">
        <h1 className="m-0 text-2xl text-white">Управление группами</h1>
        <div className="flex flex-wrap items-center gap-3">
          <Button classNames="w-auto" onClick={() => setIsRegisterUserModalOpen(true)}>
            Добавить пользователя
          </Button>
          <Button
            classNames="w-auto"
            onClick={() => setIsCreateGroupModalOpen(true)}
            variant="green"
          >
            Создать группу
          </Button>
        </div>
      </div>

      <div className="mb-6 flex-1 px-6">
        <div className="mx-auto h-full max-w-[1200px] overflow-hidden rounded-lg bg-white shadow">
          <div className="overflow-auto">
            <table className="min-w-full divide-y divide-grey-1200">
              <thead className="bg-grey-1100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">
                    Название
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">
                    Описание
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-grey-1200 bg-white">
                {groups.length ? (
                  groups.map(group => (
                    <tr key={group.id}>
                      <td className="px-4 py-3 text-sm text-black">{group.group_name}</td>
                      <td className="px-4 py-3 text-sm text-black">
                        {group.group_description || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            classNames="w-auto px-4"
                            onClick={() => {
                              setSelectedGroup(group);
                              setActiveTab('members');
                            }}
                            size="small"
                          >
                            Подробности
                          </Button>
                          <Button
                            classNames="w-auto px-4"
                            onClick={() => handleDeleteGroup(group.id)}
                            size="small"
                            variant="orange"
                          >
                            Удалить
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-4 py-6 text-center text-sm text-grey-300" colSpan={3}>
                      Группы пока не созданы
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedGroup && (
        <div className="flex-1 px-6 pb-6">
          <div className="mx-auto h-full max-w-[1200px] overflow-hidden rounded-lg bg-white shadow">
            <div className="border-b border-grey-1200 px-6 py-4">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-black">{selectedGroup.group_name}</h2>
                <p className="text-sm text-grey-300">
                  {selectedGroup.group_description || 'Описание не заполнено'}
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  className={`rounded px-4 py-2 text-sm font-medium transition-all ${
                    activeTab === 'members'
                      ? 'bg-blue-100 text-white'
                      : 'bg-grey-1100 text-grey-300 hover:bg-grey-1200'
                  }`}
                  onClick={() => setActiveTab('members')}
                  type="button"
                >
                  Участники
                </button>
                <button
                  className={`rounded px-4 py-2 text-sm font-medium transition-all ${
                    activeTab === 'projects'
                      ? 'bg-blue-100 text-white'
                      : 'bg-grey-1100 text-grey-300 hover:bg-grey-1200'
                  }`}
                  onClick={() => setActiveTab('projects')}
                  type="button"
                >
                  Проекты
                </button>
              </div>
            </div>

            <div className="max-h-[400px] overflow-auto">
              {activeTab === 'members' ? (
                <MembersTab
                  groupId={selectedGroup.id}
                  members={selectedGroup.users}
                  onUpdate={fetchGroups}
                />
              ) : (
                <ProjectsTab
                  groupId={selectedGroup.id}
                  onUpdate={fetchGroups}
                  projects={selectedGroup.projects}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {isCreateGroupModalOpen && (
        <Modal classNames="z-130 bg-black/40 px-4" onClose={closeCreateGroupModal}>
          <div className="w-[min(90vw,32rem)]">
            <h2 className="mb-6 text-xl font-semibold text-black">Создать группу</h2>

            <form className="flex flex-col gap-4" onSubmit={handleCreateGroup}>
              <Input
                containerClassNames="w-full"
                error={createGroupErrors.name}
                inputClassNames="text-black"
                label="Название"
                name="name"
                onChange={handleCreateGroupInputChange}
                value={createGroupValues.name}
                variant="grey"
              />

              <div>
                <div className={labelClassName}>Описание</div>
                <textarea
                  className={textareaFieldClassName}
                  name="description"
                  onChange={handleCreateGroupInputChange}
                  value={createGroupValues.description}
                />
              </div>

              <div className="mt-2 flex justify-end gap-3">
                <Button
                  classNames="w-auto px-5"
                  onClick={closeCreateGroupModal}
                  type="button"
                  variant="grey"
                >
                  Отмена
                </Button>
                <Button classNames="w-auto px-5" type="submit" variant="green">
                  Создать
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {isRegisterUserModalOpen && (
        <Modal classNames="z-130 bg-black/40 px-4" onClose={closeRegisterUserModal}>
          <div className="w-[min(90vw,32rem)]">
            <h2 className="mb-6 text-xl font-semibold text-black">Регистрация пользователя</h2>

            <form className="flex flex-col gap-4" onSubmit={handleRegisterUser}>
              <Input
                containerClassNames="w-full"
                error={registerUserErrors.email}
                inputClassNames="text-black"
                label="Email"
                name="email"
                onChange={handleRegisterUserChange}
                type="email"
                value={registerUserValues.email}
                variant="grey"
              />

              <Input
                containerClassNames="w-full"
                error={registerUserErrors.full_name}
                inputClassNames="text-black"
                label="Полное имя"
                name="full_name"
                onChange={handleRegisterUserChange}
                value={registerUserValues.full_name}
                variant="grey"
              />

              <Input
                containerClassNames="w-full"
                error={registerUserErrors.user_name}
                inputClassNames="text-black"
                label="Имя пользователя"
                name="user_name"
                onChange={handleRegisterUserChange}
                value={registerUserValues.user_name}
                variant="grey"
              />

              <div>
                <div className={labelClassName}>Роль</div>
                <select
                  className={nativeFieldClassName}
                  name="role"
                  onChange={handleRegisterUserChange}
                  value={registerUserValues.role}
                >
                  <option value="">Выберите роль пользователя</option>
                  {roleOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {registerUserErrors.role && (
                  <div className={errorClassName}>{registerUserErrors.role}</div>
                )}
              </div>

              <div className="mt-2 flex justify-end gap-3">
                <Button
                  classNames="w-auto px-5"
                  onClick={closeRegisterUserModal}
                  type="button"
                  variant="grey"
                >
                  Отмена
                </Button>
                <Button classNames="w-auto px-5" type="submit" variant="green">
                  Зарегистрировать
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
};
